import { Box, Button, Flex, Input, Text } from '@ttoss/ui';
import { IS_PRODUCTION } from '../../constants/featureFlag';
import { Icon } from '@iconify/react';
import { env } from '@siflor/env';
import { useNavigation } from '../../providers/NavigationProvider/NavigationProvider';
import { usePlacesAutocomplete } from '@ttoss/google-maps';
import HorizontalLine from '../HorizontalLine/HorizontalLine';
import Menu from '../Menu/Menu';
import React from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';

export type HeaderProps = {
  onEnter?: (text: string) => void;
  initialSearchText?: string;
  title?: string;
};

type Suggestion = {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  saved?: boolean;
};

const Header = ({ onEnter, initialSearchText, title }: HeaderProps) => {
  const navigation = useNavigation();

  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  const [menuIsOpen, setMenuIsOpen] = React.useState(false);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    cacheKey: env.googleMaps.apiKey,
  });

  React.useEffect(() => {
    if (!!initialSearchText && initialSearchText !== value) {
      setValue(initialSearchText || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchText, setValue]);

  const clearText = () => {
    setValue('', false);
    clearSuggestions();
  };

  const boxSearchRef = useOnclickOutside(() => {
    setTimeout(() => {
      clearSuggestions();
    }, 150);
  });

  const handleInput: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = ({ description }: { description: string }) => {
    return () => {
      setValue(description, false);
      onEnter?.(description);
      clearSuggestions();
    };
  };

  const FAKE_SUGGESTIONS = [
    {
      place_id: '1',
      description: 'Fazenda Santa Amélia Zona Rural, Nova Xavantina - TO',
      structured_formatting: {
        main_text: 'Fazenda Santa Amélia',
        secondary_text: 'Zona Rural, Nova Xavantina - TO',
      },
      saved: true,
    },
    {
      place_id: '2',
      description: 'Fazenda São José Zona Rural, Nova Xavantina - TO',
      structured_formatting: {
        main_text: 'Fazenda São José',
        secondary_text: 'Zona Rural, Nova Xavantina - TO',
      },
      saved: true,
    },
  ].filter(() => {
    return !IS_PRODUCTION;
  });

  const renderSuggestions = () => {
    return ([...FAKE_SUGGESTIONS, ...data] as Suggestion[]).map(
      (suggestion, idx, list) => {
        const {
          place_id,
          structured_formatting: { main_text, secondary_text },
          saved,
        } = suggestion;

        return (
          <>
            <Flex
              sx={{
                minHeight: 32,
                paddingLeft: 6,
                alignItems: 'center',
                gap: 6,
                paddingTop: 6,
                ':hover': {
                  backgroundColor: 'muted',
                },
              }}
              as="li"
              key={`place-${place_id}`}
              onClick={handleSelect(suggestion)}
            >
              <Text sx={{ color: 'neutral' }}>
                <Icon
                  icon={
                    saved
                      ? 'ic:baseline-bookmark-border'
                      : 'ic:outline-location-on'
                  }
                />
              </Text>

              <Flex sx={{ flexDirection: 'column', width: '100%' }}>
                <Text sx={{ color: 'dark' }} as="strong">
                  {main_text}
                </Text>
                <Text as="p" sx={{ marginBottom: 6 }}>
                  <Text as="small" sx={{ color: 'text' }}>
                    {secondary_text}
                  </Text>
                </Text>
                {idx < list.length - 1 && (
                  <HorizontalLine sx={{ borderTop: 'solid 1px' }} />
                )}
              </Flex>
            </Flex>
          </>
        );
      }
    );
  };

  return (
    <Flex sx={{ flexDirection: 'column', position: 'relative' }}>
      {menuIsOpen && (
        <Menu
          {...navigation}
          sx={{
            position: 'fixed',
            width: '100%',
            minHeight: '100vh',
            zIndex: [3],
            backgroundColor: 'white',
            overflowY: 'scroll',
            maxHeight: '100%',
          }}
          onLogout={navigation.onLogout}
          onCloseMenu={() => {
            return setMenuIsOpen(false);
          }}
        />
      )}
      <Flex
        sx={{
          backgroundColor: 'white',
          alignItems: 'center',
          zIndex: 2,
          height: '68px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
          paddingY: [5],
          paddingX: [5],
        }}
      >
        <Button
          aria-label="button-menu"
          onClick={() => {
            return setMenuIsOpen(true);
          }}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            backgroundColor: 'transparent',
            color: 'text',
            fontSize: 4,
            marginRight: [5],
          }}
        >
          <Icon icon="ic:round-menu" />
        </Button>

        {title ? (
          <Flex
            sx={{
              justifyContent: 'center',
              width: 'calc(100% - 70px)',
            }}
          >
            <Text
              sx={{ fontWeight: 'bold', fontSize: 3, fontFamily: 'heading' }}
            >
              {title}
            </Text>
          </Flex>
        ) : (
          <Flex
            sx={{
              width: '100%',
              position: 'relative',
              height: '100%',
            }}
            ref={boxSearchRef}
          >
            <Input
              aria-roledescription="input-search-autocomplete"
              className="header-search-input"
              id="input-search-autocomplete"
              variant="input-search"
              sx={{
                width: '100%',
                height: '100%',
              }}
              onPaste={(e) => {
                const pastedText = e.clipboardData.getData('text/plain');

                onEnter?.(pastedText);
                setValue(pastedText);
              }}
              placeholder="Digite um lugar"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  onEnter?.(value);
                  clearSuggestions();

                  closeButtonRef?.current?.focus?.();
                }
              }}
              value={value}
              onChange={handleInput}
              disabled={!ready}
            />
          </Flex>
        )}

        {!!value && (
          <Button
            ref={closeButtonRef}
            onClick={clearText}
            aria-label="clear"
            sx={{
              backgroundColor: 'transparent',
              position: 'absolute',
              right: 6,
              fontSize: 4,
              display: 'flex',
              color: 'muted',
              cursor: 'pointer',
              ':focus-within': {
                outline: 0,
              },
            }}
          >
            <Icon icon="ic:round-close" />
          </Button>
        )}
      </Flex>
      {status === 'OK' && (
        <Box
          as="ul"
          sx={{
            position: 'absolute',
            zIndex: 1,
            top: 55,
            width: '100%',
            paddingTop: 6,
            boxShadow: 2,
            listStyle: 'none',
            paddingInlineStart: 0,
            backgroundColor: 'background',
          }}
        >
          {renderSuggestions()}
        </Box>
      )}
    </Flex>
  );
};

export default Header;
